import {EventModel} from 'event/models/Event'
import MeetingProvider from 'organization/Event/MeetingProvider'
import {SaveEventData} from 'organization/Event/Settings/SaveEvent'
import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {
  useRouteOrganizationEventId,
  useRouteOrganizationId,
} from 'utils/lib/url'

export type SyncEventTicketRow = {
  id?: number
  name: string
  price: number
  ticket_features: string[]
  sort_order: number
}

export interface OrganizationEventContextProps {
  event: EventModel | null
  get: () => Promise<void>
  updateEventTemplate: (eventTemplate: EventModel['template']) => void
  updateEvent: (data: SaveEventData) => Promise<string | boolean | undefined>
  syncEventTickets: (tickets: SyncEventTicketRow[]) => Promise<boolean>
  makeEventPublic: () => Promise<string | boolean | undefined>
  endEvent: () => Promise<string | boolean | undefined>
  updateEventCertificate: (
    certificate_json: EventModel['certificate'],
  ) => Promise<string | boolean | undefined>
  loading: boolean
  refreshEvent: () => void
}

export const OrganizationEventContext = React.createContext<
  OrganizationEventContextProps | undefined
>(undefined)

export default function OrganizationEventProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [event, setEvent] = useState<EventModel | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const eventId = useRouteOrganizationEventId()
  const organizationId = useRouteOrganizationId()

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    setLoading(true)
    await apiClient
      .get(`api/organization/${organizationId}/event/${eventId}`)
      .then((response) => {
        setEvent(response)
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const updateEventTemplate = async (eventTemplate: EventModel['template']) => {
    if (loading) return

    setLoading(true)
    await apiClient
      .post(`api/event/${eventId}/template`, {
        template: JSON.stringify(eventTemplate),
      })
      .then((updatedTemplate: EventModel['template']) => {
        setEvent((currEvent) => {
          if (!currEvent) return null
          return {
            ...currEvent,
            template: updatedTemplate,
          }
        })
        return true
      })
      .finally(() => setLoading(false))
  }

  const updateEventCertificate = async (
    certificate: EventModel['certificate'],
  ) => {
    if (loading) return

    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/certificate`, {
        certificate_json: certificate?.certificate_json,
      })
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return true

    return false
  }

  const updateEvent = async (data: SaveEventData) => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/update`, data)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return true

    return false
  }

  const syncEventTickets = async (tickets: SyncEventTicketRow[]) => {
    if (loading) return false
    setLoading(true)
    const result = await apiClient
      .post(
        `api/event/${eventId}/tickets/sync`,
        {tickets},
        {headers: {'Content-Type': 'application/json'}},
      )
      .then((updated: EventModel) => {
        setEvent(updated)
        return true
      })
      .catch((error) => {
        const data = error.response?.data
        const msg =
          (typeof data?.message === 'string' && data.message) ||
          data?.errors?.tickets?.[0] ||
          data?.errors?.tickets
        toast.warning(typeof msg === 'string' ? msg : 'Could not save tickets.')
        return false
      })
      .finally(() => setLoading(false))

    return Boolean(result)
  }

  const makeEventPublic = async () => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/make-public`)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    return result
  }

  const endEvent = async () => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/end`)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    return result
  }

  const refreshEvent = () => {
    get()
  }

  return (
    <OrganizationEventContext.Provider
      value={{
        event,
        get,
        updateEventTemplate,
        updateEvent,
        syncEventTickets,
        makeEventPublic,
        endEvent,
        updateEventCertificate,
        loading,
        refreshEvent,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && <MeetingProvider>{props.children}</MeetingProvider>}
    </OrganizationEventContext.Provider>
  )
}

export function useOrganizationEvent() {
  const context = React.useContext(OrganizationEventContext)
  if (context === undefined) {
    throw new Error(
      'useOrganizationEvent must be used within OrganizationEventProvider',
    )
  }
  return context
}
